/**
 * Feature Request Page — list requests, create new, vote.
 * Tabs: Most Voted / Newest. Status badges. Optimistic vote toggle.
 */

import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ArrowUp, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@turnifly/shared/ui'
import { Input } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { useAuthStore } from '@turnifly/shared/stores'
import {
  fetchFeatureRequests,
  createFeatureRequest,
  voteFeatureRequest,
  unvoteFeatureRequest,
} from '@turnifly/shared/api'
import type { FeatureRequestItem } from '@turnifly/shared/api'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

interface FeatureRequestPageProps {
  isOpen: boolean
  onClose: () => void
}

type SortMode = 'votes' | 'newest'

export const FeatureRequestPage: React.FC<FeatureRequestPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const [sortBy, setSortBy] = useState<SortMode>('votes')
  const [isCreating, setCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDesc, setNewDesc] = useState('')

  const { data: requests, isLoading } = useQuery({
    queryKey: ['feature-requests', user?.id, sortBy],
    queryFn: () => fetchFeatureRequests(user!.id, sortBy),
    enabled: !!user?.id && isOpen,
  })

  const createMutation = useMutation({
    mutationFn: () => createFeatureRequest(user!.id, newTitle, newDesc),
    onSuccess: () => {
      setNewTitle('')
      setNewDesc('')
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ['feature-requests'] })
    },
  })

  const voteMutation = useMutation({
    mutationFn: async ({ featureId, isVoted }: { featureId: string; isVoted: boolean }) => {
      if (isVoted) {
        await unvoteFeatureRequest(user!.id, featureId)
      } else {
        await voteFeatureRequest(user!.id, featureId)
      }
    },
    onMutate: async ({ featureId, isVoted }) => {
      const key = ['feature-requests', user?.id, sortBy]
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)

      queryClient.setQueryData<FeatureRequestItem[]>(key, (old) =>
        (old ?? []).map((r) =>
          r.id === featureId
            ? { ...r, is_voted_by_me: !isVoted, votes_count: r.votes_count + (isVoted ? -1 : 1) }
            : r,
        ),
      )
      return { previous, key }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feature-requests'] })
    },
  })

  const handleVote = useCallback((featureId: string, isVoted: boolean): void => {
    hapticImpact('light')
    voteMutation.mutate({ featureId, isVoted })
  }, [voteMutation])

  const handleCreate = (): void => {
    if (!newTitle.trim()) return
    createMutation.mutate()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh] lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-2 shrink-0">
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('featureRequest.title')}</h2>
              <button
                onClick={() => setCreating(!isCreating)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent"
              >
                <Plus size={20} className="text-primary" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {/* Sort tabs */}
              <div className="flex gap-2 mb-3">
                {(['votes', 'newest'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setSortBy(mode)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all tap-highlight-transparent ${
                      sortBy === mode
                        ? 'bg-primary/15 text-primary'
                        : 'bg-white/[0.04] text-text-hint hover:bg-white/[0.06]'
                    }`}
                  >
                    {mode === 'votes' ? t('featureRequest.mostVoted') : t('featureRequest.newest')}
                  </button>
                ))}
              </div>

              {/* Create form */}
              <AnimatePresence>
                {isCreating && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mb-3"
                  >
                    <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] space-y-3">
                      <Input
                        placeholder={t('featureRequest.titlePlaceholder')}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        maxLength={100}
                      />
                      <textarea
                        placeholder={t('featureRequest.descPlaceholder')}
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-text text-sm placeholder:text-text-hint/50 resize-none focus:outline-none focus:border-primary/30 transition-colors"
                      />
                      <Button
                        variant="primary" size="sm" fullWidth
                        onClick={handleCreate}
                        isLoading={createMutation.isPending}
                        disabled={!newTitle.trim()}
                      >
                        {t('featureRequest.submit')}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Request list */}
              {isLoading ? (
                <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
              ) : !requests?.length ? (
                <div className="py-12 text-center text-text-hint text-sm">
                  {t('featureRequest.noRequests')}
                </div>
              ) : (
                <div className="space-y-2">
                  {requests.map((req) => (
                    <RequestCard key={req.id} request={req} onVote={handleVote} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ── Request Card ────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, 'primary' | 'warning' | 'success' | 'danger'> = {
  open: 'primary',
  in_progress: 'warning',
  done: 'success',
  closed: 'danger',
}

interface RequestCardProps {
  request: FeatureRequestItem
  onVote: (id: string, isVoted: boolean) => void
}

const RequestCard: React.FC<RequestCardProps> = React.memo(({ request, onVote }) => {
  const { t } = useTranslation()

  return (
    <div className="flex gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
      {/* Vote button */}
      <button
        onClick={() => onVote(request.id, request.is_voted_by_me ?? false)}
        className={`flex flex-col items-center justify-center w-12 shrink-0 rounded-lg transition-all tap-highlight-transparent ${
          request.is_voted_by_me
            ? 'bg-primary/15 text-primary'
            : 'bg-white/[0.04] text-text-hint hover:bg-white/[0.06]'
        }`}
      >
        <ArrowUp size={14} strokeWidth={2.5} />
        <span className="text-xs font-bold mt-0.5">{request.votes_count}</span>
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-text leading-tight">{request.title}</p>
          <Badge variant={STATUS_VARIANTS[request.status] ?? 'primary'} className="text-[9px] shrink-0">
            {t(`featureRequest.status.${request.status}`)}
          </Badge>
        </div>
        {request.description && (
          <p className="text-xs text-text-hint mt-1 line-clamp-2">{request.description}</p>
        )}
      </div>
    </div>
  )
})
RequestCard.displayName = 'RequestCard'
